import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichEditor } from "@/components/RichEditor";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Save, FileText } from "lucide-react";

const BLANK = { id: null, name: "", subject: "", body_html: "" };

export default function Templates() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [current, setCurrent] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reply-templates");
      setTemplates(data.templates || []);
    } catch (e) { if (e.response?.status === 401) nav("/login"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const textFromHtml = (html) => {
    const d = document.createElement("div");
    d.innerHTML = html || "";
    return (d.textContent || "").trim();
  };

  const pick = (t) => setCurrent({ id: t.id, name: t.name, subject: t.subject || "", body_html: t.body_html || "" });
  const newOne = () => setCurrent(BLANK);

  const save = async () => {
    if (!current.name.trim()) return toast.error("Give the template a name");
    setSaving(true);
    try {
      const payload = { name: current.name, subject: current.subject, body_html: current.body_html, body_text: textFromHtml(current.body_html) };
      if (current.id) {
        const { data } = await api.put(`/reply-templates/${current.id}`, payload);
        setCurrent({ id: data.template.id, name: data.template.name, subject: data.template.subject, body_html: data.template.body_html });
        toast.success("Template updated");
      } else {
        const { data } = await api.post("/reply-templates", payload);
        setCurrent({ id: data.template.id, name: data.template.name, subject: data.template.subject, body_html: data.template.body_html });
        toast.success("Template saved");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!current.id) return;
    try {
      await api.delete(`/reply-templates/${current.id}`);
      toast.success("Template deleted");
      setCurrent(BLANK);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex min-h-screen bg-[#FDFDFB]">
      <Sidebar />
      <main className="flex-1 max-w-7xl mx-auto px-6 md:px-10 py-10 w-full">
        <div className="mb-8">
          <div className="overline">Reply templates</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Answer replies in one tap.</h1>
          <p className="text-sm text-[#5C6661] mt-2 max-w-xl">Save polished responses — thanks-for-replying, availability, portfolio share — then fire them off from History without leaving JobKnocker.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="space-y-3">
            <Button onClick={newOne} className="w-full rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" data-testid="new-template-btn"><Plus className="w-4 h-4 mr-2" /> New template</Button>
            {templates.length === 0 && (
              <Card className="p-4 border-dashed border-[#C7D6CC] bg-transparent">
                <div className="text-xs text-[#5C6661]" data-testid="templates-empty">No templates yet. Start with a quick “thanks for the reply” and reuse it forever.</div>
              </Card>
            )}
            {templates.map((t) => (
              <Card
                key={t.id}
                onClick={() => pick(t)}
                data-testid={`template-item-${t.id}`}
                className={`p-4 cursor-pointer border-[#E2E8E4] hover:border-[#2D4A3E]/40 transition-colors ${current.id === t.id ? "border-[#2D4A3E] bg-[#E7F0EB]/30" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-[#2D4A3E]" />
                  <div className="font-medium text-[#121413] text-sm truncate">{t.name}</div>
                </div>
                {t.subject && <div className="text-xs text-[#5C6661] mt-1 truncate">Subject: {t.subject}</div>}
              </Card>
            ))}
          </div>

          <div className="md:col-span-2">
            <Card className="p-6 border-[#E2E8E4]">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="tpl-name">Template name</Label>
                  <Input id="tpl-name" data-testid="template-name" value={current.name} onChange={(e) => setCurrent((c) => ({ ...c, name: e.target.value }))} placeholder="e.g. Thanks for replying" className="mt-1.5 h-11 rounded-lg" />
                </div>
                <div>
                  <Label htmlFor="tpl-subject">Subject (optional)</Label>
                  <Input id="tpl-subject" data-testid="template-subject" value={current.subject} onChange={(e) => setCurrent((c) => ({ ...c, subject: e.target.value }))} placeholder="Re: your reply" className="mt-1.5 h-11 rounded-lg" />
                </div>
                <div>
                  <Label>Message</Label>
                  <div className="mt-1.5">
                    <RichEditor value={current.body_html} onChange={(html) => setCurrent((c) => ({ ...c, body_html: html }))} placeholder="Hi {{first_name}} — thanks for getting back to me…" data-testid="template-body" />
                  </div>
                  <div className="text-[11px] text-[#5C6661] mt-2">Tip: keep it short and warm — mention availability, next steps, and a portfolio link if relevant.</div>
                </div>
                <div className="flex items-center gap-2 justify-end pt-2 border-t border-[#E2E8E4]">
                  {current.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="rounded-full border-[#FCA5A5] text-[#C53030] hover:bg-[#FEF2F2]" data-testid="delete-template-btn"><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this template?</AlertDialogTitle>
                          <AlertDialogDescription>This can't be undone. History rows using this template won't be affected.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={remove} className="bg-[#C53030] hover:bg-[#B12525]" data-testid="delete-template-confirm">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <Button onClick={save} disabled={saving} className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" data-testid="save-template-btn">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : current.id ? "Update template" : "Save template"}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
